import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, setAccessToken, setRefreshToken, silentRefresh } from '../services/api';
import { passwordService } from '../services/passwordService';
import { logger } from '../utils/logger';
import type { AuthRequest, UserProfile } from '../types/api';

interface User extends UserProfile {
  // For backward compatibility, map user_id to id
  id: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (accessToken: string, refreshToken?: string) => Promise<void>;
  verifyTwoFactor: (userId: string, totpCode?: string, backupCode?: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
  isTwoFactorModalOpen: boolean;
  openTwoFactorModal: (userId: string) => void;
  closeTwoFactorModal: () => void;
  twoFactorUserId: string | null;
  twoFactorError: string | null;
  clearTwoFactorError: () => void;
  // Password change functionality
  isPasswordChangeModalOpen: boolean;
  openPasswordChangeModal: () => void;
  closePasswordChangeModal: () => void;
  checkPasswordStatus: () => Promise<void>;
  // Legal acceptance
  isLegalModalOpen: boolean;
  acceptLegalTerms: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string | null>(null); // State for one-time redirect
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  // Password change modal state
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);

  // Legal acceptance modal state
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

  const clearTwoFactorError = useCallback(() => {
    setTwoFactorError(null);
  }, []);

  const openTwoFactorModal = useCallback((userId: string) => {
    setTwoFactorUserId(userId);
    setIsTwoFactorModalOpen(true);
  }, []);

  const closeTwoFactorModal = useCallback(() => {
    setIsTwoFactorModalOpen(false);
    setTwoFactorUserId(null);
    // If the user manually closes the modal, the auth process is cancelled.
    setIsLoading(false);
  }, []);

  // Password change modal functions
  const openPasswordChangeModal = useCallback(() => {
    setIsPasswordChangeModalOpen(true);
  }, []);

  const closePasswordChangeModal = useCallback(() => {
    setIsPasswordChangeModalOpen(false);
  }, []);

  // Check password status and show modal if needed
  const checkPasswordStatus = useCallback(async () => {
    try {
      logger.log('[AuthContext] Checking password status...');
      const status = await passwordService.getPasswordStatus();
      logger.log('[AuthContext] Password status received:', status);
      if (status.force_password_change) {
        logger.log('[AuthContext] Force password change required - opening modal');
        openPasswordChangeModal();
      } else {
        logger.log('[AuthContext] No password change required');
      }
    } catch (error) {
      logger.error('[AuthContext] Failed to check password status:', error);
    }
  }, [openPasswordChangeModal]);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Attempt silent token refresh to restore in-memory session after page reload
        const token = await silentRefresh();
        if (token) {
          const userProfile = await authAPI.getUserStatus();
          const authenticatedUser: User = {
            ...userProfile,
            id: userProfile.user_id,
          };
          setUser(authenticatedUser);
        }
      } catch (error) {
        logger.error('[AuthContext] Failed to restore session:', error);
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Effect for one-time redirect (only after login/2FA)
  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
      // Reset redirectPath after navigation, but only if current path is not already the target
      setTimeout(() => setRedirectPath(null), 100); // allow navigation to complete
    }
    // No other navigation logic here!
  }, [redirectPath, navigate]);

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const userProfile = await authAPI.getUserStatus();
      const updatedUser: User = {
        ...userProfile,
        id: userProfile.user_id,
      };
      setUser(updatedUser);
    } catch (error) {
      logger.error('[AuthContext] Failed to refresh user:', error);
      throw error;
    }
  }, []);

  // Process successful login (shared by local and OAuth)
  const processSuccessfulLogin = useCallback(async (accessToken: string, refreshToken?: string) => {
    setAccessToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken);
    const userProfile = await authAPI.getUserStatus();
    const authenticatedUser: User = { ...userProfile, id: userProfile.user_id };
    setUser(authenticatedUser);
    setIsLoading(false);

    logger.log('[AuthContext] Login successful, checking password status...');
    await checkPasswordStatus();

    // Check if legal acceptance is required
    if (!userProfile.legal_acceptance_timestamp) {
      logger.log('[AuthContext] Legal acceptance required');
      setIsLegalModalOpen(true);
    } else {
      // Only set redirectPath if we're on /login or /
      if (globalThis.location.pathname === '/login' || globalThis.location.pathname === '/') {
        setRedirectPath('/dashboard');
      }
    }
  }, [checkPasswordStatus]);

  // Login with email/password (local auth)
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    logger.log('[AuthContext] Starting login for:', email);
    setIsLoading(true);
    try {
      const credentials: AuthRequest = { email, password };
      const loginResponse = await authAPI.login(credentials);
      logger.log('[AuthContext] Login response received:', loginResponse);

      if (loginResponse.two_factor_required && loginResponse.user_id) {
        logger.log('[AuthContext] 2FA required for user:', loginResponse.user_id);
        openTwoFactorModal(loginResponse.user_id);
        // isLoading remains true until 2FA is resolved
        return;
      }

      if (loginResponse.access_token && loginResponse.access_token !== '2FA_REQUIRED') {
        await processSuccessfulLogin(loginResponse.access_token, loginResponse.refresh_token);
        return;
      }

    } catch (error) {
      setIsLoading(false);
      logger.error('[AuthContext] Login failed:', error);
      throw error;
    }
  }, [openTwoFactorModal, processSuccessfulLogin]);

  // Login with token (OAuth flow)
  const loginWithToken = useCallback(async (accessToken: string, refreshToken?: string): Promise<void> => {
    logger.log('[AuthContext] Starting OAuth token login');
    setIsLoading(true);
    try {
      await processSuccessfulLogin(accessToken, refreshToken);
    } catch (error) {
      setIsLoading(false);
      logger.error('[AuthContext] OAuth token login failed:', error);
      throw error;
    }
  }, [processSuccessfulLogin]);

  const verifyTwoFactor = useCallback(async (userId: string, totpCode?: string, backupCode?: string): Promise<boolean> => {
    logger.log('[AuthContext] Verifying 2FA for user:', userId);
    logger.log('[AuthContext] TOTP code:', totpCode ? `${totpCode.length} chars` : 'none');
    logger.log('[AuthContext] Backup code:', backupCode ? `${backupCode.length} chars` : 'none');

    // Clear any previous error
    setTwoFactorError(null);

    if (!totpCode && !backupCode) {
      logger.error('[AuthContext] Neither TOTP nor backup code provided');
      return false;
    }

    // isLoading is already true from the login() call
    try {
      const loginResponse = await authAPI.verifyTwoFactorLogin({
        user_id: userId,
        totp_code: totpCode || undefined,
        backup_code: backupCode || undefined,
      });

      if (loginResponse.access_token) {
        setAccessToken(loginResponse.access_token);
        const userProfile = await authAPI.getUserStatus();

        const authenticatedUser: User = { ...userProfile, id: userProfile.user_id };

        logger.log('[AuthContext] About to set user state and close modal');
        // Batch state updates together for atomic re-render
        setUser(authenticatedUser);
        setIsTwoFactorModalOpen(false);
        setTwoFactorUserId(null);
        setIsLoading(false); // Set loading to false only on final success

        // Check password status after successful 2FA
        await checkPasswordStatus();

        // Check if legal acceptance is required
        if (!userProfile.legal_acceptance_timestamp) {
          setIsLegalModalOpen(true);
        } else {
          // Only set redirectPath if we're on /login or /
          if (globalThis.location.pathname === '/login' || globalThis.location.pathname === '/') {
            setRedirectPath('/dashboard');
          }
        }

        return true;
      }

      // Verification failed, so we stop the loading indicator to allow another attempt.
      setIsLoading(false);
      return false;
    } catch (error: any) {
      logger.error('[AuthContext] 2FA verification failed with error:', error);
      setIsLoading(false); // Also stop loading on error

      // Extract error message from backend response
      let errorMessage = 'An unexpected error occurred.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Set error message instead of throwing
      setTwoFactorError(errorMessage);
      return false;
    }
  }, [checkPasswordStatus]);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      logger.error('[AuthContext] Logout error:', error);
    } finally {
      // Clear in-memory tokens and OAuth ephemeral data
      setAccessToken(null);
      setRefreshToken(null);
      ['oauth_state', 'oauth_email', 'pkce_verifier'].forEach(key => sessionStorage.removeItem(key));
      Object.keys(localStorage).forEach(key => {
        if (key.toLowerCase().includes('auth') || key.toLowerCase().includes('user')) {
          localStorage.removeItem(key);
        }
      });
      setUser(null);
      // Clear 2FA state on logout
      closeTwoFactorModal();
      // Clear legal modal state
      setIsLegalModalOpen(false);
    }
  }, [closeTwoFactorModal]);

  // Accept legal terms
  const acceptLegalTerms = useCallback(async () => {
    try {
      await authAPI.acceptLegalTerms();
      await refreshUser(); // Refresh user to get updated legal_acceptance_timestamp
      setIsLegalModalOpen(false);

      // Navigate to dashboard after acceptance
      if (globalThis.location.pathname === '/login' || globalThis.location.pathname === '/') {
        setRedirectPath('/dashboard');
      }
    } catch (error) {
      logger.error('[AuthContext] Failed to accept legal terms:', error);
      throw error;
    }
  }, [refreshUser]);

  const value = useMemo(() => ({
    user,
    login,
    loginWithToken,
    verifyTwoFactor,
    logout,
    refreshUser,
    isLoading,
    isTwoFactorModalOpen,
    openTwoFactorModal,
    closeTwoFactorModal,
    twoFactorUserId,
    twoFactorError,
    clearTwoFactorError,
    isPasswordChangeModalOpen,
    openPasswordChangeModal,
    closePasswordChangeModal,
    checkPasswordStatus,
    isLegalModalOpen,
    acceptLegalTerms,
  }), [
    user,
    login,
    loginWithToken,
    verifyTwoFactor,
    logout,
    refreshUser,
    isLoading,
    isTwoFactorModalOpen,
    openTwoFactorModal,
    closeTwoFactorModal,
    twoFactorUserId,
    twoFactorError,
    clearTwoFactorError,
    isPasswordChangeModalOpen,
    openPasswordChangeModal,
    closePasswordChangeModal,
    checkPasswordStatus,
    isLegalModalOpen,
    acceptLegalTerms,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
